import React, { useState, useEffect, useMemo } from 'react';
import BBALoader from '../components/BBALoader';
import { Search, Filter, Lock, FileText, File, Edit, Download, X, Scissors, FileSpreadsheet, Upload, Plus, ArrowLeft, Book, PlayCircle, Sparkles, Pin } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import { FaGraduationCap, FaPenNib, FaCheckCircle } from 'react-icons/fa';
import { getAllCourses, createCourse, deleteCourse, updateCourse, uploadCourseThumbnail, getCourseContentActivity } from '../services/firebase/courses';
import { useTheme } from '../contexts/ThemeContext';
import BackButton from '../components/BackButton';
import AddButton from '../components/AddButton';
import KpiTile from '../components/finance/KpiTile';
import Modal from '../components/Modal';
import PortalBackground from '../components/PortalBackground';

// Courses with content added/updated within this window float to the top of the grid.
const NEW_CONTENT_WINDOW_MS = 30 * 24 * 60 * 60 * 1000;

const formatTimeAgo = (date) => {
  if (!date) return '';
  const now = new Date();
  const d = typeof date === 'number' ? new Date(date) : new Date(date);
  const diffInSeconds = Math.floor((now - d) / 1000);
  if (diffInSeconds < 60) return 'Just now';
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
  if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 86400)}d ago`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const feedCategoryColor = (category) => {
  switch (category) {
    case 'Sales': return 'bg-red-600';
    case 'Operations': return 'bg-blue-600';
    case 'Technology': return 'bg-purple-600';
    case 'Finance': return 'bg-green-600';
    case 'Marketing': return 'bg-orange-500';
    case 'HR': return 'bg-yellow-500';
    default: return 'bg-gray-600';
  }
};

// Remap a portal feed item to the CRM course-templates route.
const crmFeedPath = (item) =>
  item.kind === 'course'
    ? `/crm/course-templates/${item.courseId}`
    : `/crm/course-templates/${item.courseId}/lessons/${item.lessonId}`;

const CompactFeedCard = ({ item, darkMode }) => {
  const isCourse = item.kind === 'course';
  const pillLabel = isCourse ? 'New Course' : 'New Lesson';

  return (
    <Link
      to={crmFeedPath(item)}
      className={`shrink-0 w-56 rounded-lg border p-3 transition-shadow hover:shadow-md ${
        darkMode ? 'bg-gray-700/50 border-gray-600 hover:border-red-600' : 'bg-gray-50 border-gray-200 hover:border-red-200'
      }`}
    >
      {isCourse && item.thumbnailUrl ? (
        <img src={item.thumbnailUrl} alt={item.title} className="w-full h-24 object-cover rounded-md mb-2" />
      ) : (
        <div className="w-full h-24 rounded-md bg-gray-200 dark:bg-gray-600 flex items-center justify-center mb-2">
          {isCourse ? <Book className="w-8 h-8 text-red-600" /> : <PlayCircle className="w-8 h-8 text-red-600" />}
        </div>
      )}
      <span
        className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-medium mb-1.5 ${
          isCourse ? 'bg-red-600 text-white' : 'bg-gray-800 text-white dark:bg-gray-600'
        }`}
      >
        {pillLabel}
      </span>
      <h3 className={`text-sm font-semibold line-clamp-2 ${darkMode ? 'text-white' : 'text-gray-800'}`}>{item.title}</h3>
      {!isCourse && item.courseTitle && (
        <p className={`text-xs line-clamp-1 mt-0.5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{item.courseTitle}</p>
      )}
      {isCourse && item.category && (
        <span className={`inline-block mt-1 px-2 py-0.5 rounded-full text-[10px] font-medium text-white ${feedCategoryColor(item.category)}`}>
          {item.category}
        </span>
      )}
      <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{`Added ${formatTimeAgo(item.sortDate)}`}</p>
    </Link>
  );
};

const CourseTemplates = () => {
  const navigate = useNavigate();
  const { darkMode } = useTheme();
  // State for search query and selected category filter
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newCourse, setNewCourse] = useState({
    title: '',
    category: 'General',
    description: '',
    thumbnailUrl: '',
    order: 1,
    isPublished: true
  });
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [thumbnailPreview, setThumbnailPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [recentFeed, setRecentFeed] = useState([]);
  const [latestActivityByCourse, setLatestActivityByCourse] = useState({});
  const [showNewModal, setShowNewModal] = useState(false);

  // State for courses
  const [documents, setDocuments] = useState([]);
  
  // Available categories
  const categories = ['All', 'General', 'Sales', 'Operations', 'Technology', 'Finance', 'Marketing', 'HR'];
  
  // Fetch courses on component mount
  useEffect(() => {
    const fetchCourses = async () => {
      try {
        setLoading(true);
        const [courses, activity] = await Promise.all([
          getAllCourses(),
          getCourseContentActivity().catch(() => ({ items: [], latestByCourse: {} }))
        ]);
        setDocuments(courses);
        setRecentFeed(activity.items.slice(0, 4));
        setLatestActivityByCourse(activity.latestByCourse || {});
        setError(null);
      } catch (err) {
        console.error('Error fetching courses:', err);
        setError('Failed to load courses. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchCourses();
  }, []);

  // Cleanup preview URL when component unmounts or modal closes
  useEffect(() => {
    return () => {
      if (thumbnailPreview) {
        URL.revokeObjectURL(thumbnailPreview);
      }
    };
  }, [thumbnailPreview]);

  // Reset form when modal closes
  useEffect(() => {
    if (!showAddModal) {
      setNewCourse({ title: '', category: 'General', description: '', thumbnailUrl: '', order: 1, isPublished: true });
      setThumbnailFile(null);
      if (thumbnailPreview) {
        URL.revokeObjectURL(thumbnailPreview);
        setThumbnailPreview(null);
      }
    }
  }, [showAddModal, thumbnailPreview]);
  
  const newContentCutoff = Date.now() - NEW_CONTENT_WINDOW_MS;

  // Filter documents based on search query and selected category
  const filteredDocuments = documents
    .filter(doc => {
      const matchesSearch = doc.title.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory = selectedCategory === 'All' || doc.category === selectedCategory;
      return matchesSearch && matchesCategory;
    })
    // Pinned courses first, then courses with new content (newest activity on top); the rest keep their manual order.
    .sort((a, b) => {
      if (!!a.isPinned !== !!b.isPinned) return a.isPinned ? -1 : 1;
      const aDate = latestActivityByCourse[a.id] || 0;
      const bDate = latestActivityByCourse[b.id] || 0;
      const aNew = aDate >= newContentCutoff;
      const bNew = bDate >= newContentCutoff;
      if (aNew && bNew) return bDate - aDate;
      if (aNew) return -1;
      if (bNew) return 1;
      return 0;
    });

  // KPI counts for the top strip
  const kpiStats = useMemo(() => {
    const total = documents.length;
    const published = documents.filter(doc => doc.isPublished !== false).length;
    return { total, published, draft: total - published };
  }, [documents]);

  // Get category color
  const getCategoryColor = (category) => {
    switch(category) {
      case 'Sales':
        return 'bg-red-600';
      case 'Operations':
        return 'bg-blue-600';
      case 'Technology':
        return 'bg-purple-600';
      case 'Finance':
        return 'bg-green-600';
      case 'Marketing':
        return 'bg-orange-500';
      case 'HR':
        return 'bg-yellow-500';
      default:
        return 'bg-gray-600';
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setThumbnailFile(file);
      
      // Create preview URL
      const previewUrl = URL.createObjectURL(file);
      setThumbnailPreview(previewUrl);
    }
  };

  const handleAddCourse = async (e) => {
    e.preventDefault();
    try {
      setUploading(true);
      
      let thumbnailUrl = newCourse.thumbnailUrl;
      
      // Upload thumbnail if file is selected
      if (thumbnailFile) {
        const uploadResult = await uploadCourseThumbnail(thumbnailFile);
        thumbnailUrl = uploadResult.thumbnailUrl;
      }
      
      const courseData = {
        ...newCourse,
        thumbnailUrl
      };
      
      const newCourseData = await createCourse(courseData);
      setDocuments([...documents, newCourseData]);
      setShowAddModal(false);
      setNewCourse({ title: '', category: 'General', description: '', thumbnailUrl: '', order: 1, isPublished: true });
      setThumbnailFile(null);
      setThumbnailPreview(null);
    } catch (err) {
      console.error('Error creating course:', err);
      setError('Failed to create course. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleOrderChange = async (id, value) => {
    try {
      const updatedDoc = { ...documents.find(doc => doc.id === id), order: Number(value) };
      await updateCourse(id, updatedDoc);
      setDocuments(docs => docs.map(doc => doc.id === id ? updatedDoc : doc));
    } catch (err) {
      console.error('Error updating course order:', err);
      setError('Failed to update course order. Please try again.');
    }
  };

  const handleTogglePin = async (e, id) => {
    e.stopPropagation();
    const isPinned = !documents.find(doc => doc.id === id)?.isPinned;
    setDocuments(docs => docs.map(doc => doc.id === id ? { ...doc, isPinned } : doc));
    try {
      await updateCourse(id, { isPinned });
    } catch (err) {
      console.error('Error pinning course:', err);
      setDocuments(docs => docs.map(doc => doc.id === id ? { ...doc, isPinned: !isPinned } : doc));
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="text-center">
          <BBALoader size={48} className="mx-auto" />
          <p className={`mt-4 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Loading courses...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="text-center text-red-600">
          <p>{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-6 relative z-0">
      <PortalBackground />
      <style>{`
        .shine-effect {
          position: absolute;
          top: 0;
          left: -100%;
          width: 50%;
          height: 100%;
          background: linear-gradient(
            to right,
            rgba(255, 255, 255, 0) 0%,
            rgba(255, 255, 255, 0.3) 50%,
            rgba(255, 255, 255, 0) 100%
          );
          transform: skewX(-25deg);
          transition: all 0.75s ease;
        }
        
        .floating {
          animation: float 3s ease-in-out infinite;
        }
        
        @keyframes float {
          0% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-5px);
          }
          100% {
            transform: translateY(0px);
          }
        }
        
        .document-item:hover .shine-effect {
          left: 150%;
          transition: all 0.75s ease;
        }
        
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .course-card {
          transition: all 0.3s ease;
        }

        .course-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
        }

        .course-card:hover .shine-effect {
          left: 150%;
        }
      `}</style>
      {/* Content layer sits above PortalBackground (non-positioned blocks paint beneath it) */}
      <div className="relative">
      <div className="relative flex items-center justify-between mb-6">
        <BackButton onClick={() => navigate('/crm/templates')} />
        <h1 className={`text-2xl font-bold tracking-tight text-gray-900 dark:text-white absolute left-1/2 transform -translate-x-1/2`}>Course Templates</h1>
        <AddButton onClick={() => setShowAddModal(true)} />
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <KpiTile label="Total" value={String(kpiStats.total)} status="none" source="manual" showSourceBadge={false} icon={<FaGraduationCap />} loading={loading} />
        <KpiTile label="Draft" value={String(kpiStats.draft)} status="none" source="manual" showSourceBadge={false} icon={<FaPenNib />} loading={loading} />
        <KpiTile label="Published" value={String(kpiStats.published)} status="none" source="manual" showSourceBadge={false} icon={<FaCheckCircle />} loading={loading} />
        <KpiTile
          label="New Courses & Lessons"
          value={String(recentFeed.length)}
          status="none"
          source="manual"
          showSourceBadge={false}
          icon={<Sparkles className="w-3.5 h-3.5" />}
          loading={loading}
          onClick={recentFeed.length > 0 ? () => setShowNewModal(true) : undefined}
        />
      </div>

      {/* Search and Filter Section */}
      <div className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-4 mb-6`}>
        <div className="flex flex-row gap-4">
          {/* Search Bar */}
          <div className="relative flex-1">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              className={`block w-full pl-10 pr-3 py-2 border ${darkMode ? 'border-gray-600 bg-gray-700 text-white' : 'border-gray-300 bg-white text-gray-900'} rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500`}
              placeholder="Search course templates..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          
          {/* Category Filter Dropdown */}
          <div className="w-48">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Filter className="h-5 w-5 text-gray-400" />
              </div>
              <select
                className={`block w-full pl-10 pr-3 py-2 border ${darkMode ? 'border-gray-600 bg-gray-700 text-white' : 'border-gray-300 bg-white text-gray-900'} rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500`}
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
              >
                {categories.map(category => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>
      
      {/* Course Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {filteredDocuments.map(doc => (
          <div 
            key={doc.id}
            className={`course-card group bg-white dark:bg-gray-900 rounded-2xl border shadow-sm overflow-hidden hover:shadow-md hover:-translate-y-0.5 transition-all cursor-pointer relative ${
              doc.isPinned ? 'border-amber-400 ring-2 ring-amber-400 dark:border-amber-400' : 'border-gray-200 dark:border-gray-800'
            }`}
            onClick={() => !editMode && navigate(`/crm/course-templates/${doc.id}`)}
          >
            <div className="relative h-48">
              <img 
                src={doc.thumbnailUrl} 
                alt={doc.title}
                className="w-full h-full object-cover"
              />
              <div className={`absolute top-0 left-0 px-3 py-1 ${getCategoryColor(doc.category)} text-white text-sm font-medium`}>
                {doc.category}
              </div>
              {/* New Badge */}
              {(latestActivityByCourse[doc.id] || 0) >= newContentCutoff && (
                <div className="absolute bottom-2 left-2 px-2.5 py-0.5 rounded-full text-[10px] font-medium text-white bg-red-600 flex items-center gap-1 shadow-md">
                  <Sparkles className="w-2.5 h-2.5" />
                  New
                </div>
              )}
              {/* Status Dot */}
              <div className={`absolute top-2 right-2 w-3 h-3 rounded-full ${
                doc.isPublished !== false ? 'bg-green-500' : 'bg-red-500'
              } border-2 border-white shadow-md`} title={doc.isPublished !== false ? 'Published' : 'Draft'}></div>
              {/* Pin Toggle */}
              <button
                type="button"
                onClick={e => handleTogglePin(e, doc.id)}
                title={doc.isPinned ? 'Unpin course' : 'Pin to top'}
                aria-label={doc.isPinned ? 'Unpin course' : 'Pin to top'}
                className={`absolute bottom-2 right-2 h-8 rounded-full flex items-center justify-center gap-1 shadow-md transition-all ${
                  doc.isPinned
                    ? 'px-2.5 bg-gradient-to-r from-amber-300 to-yellow-500 text-amber-950 text-[11px] font-semibold'
                    : 'w-8 bg-white/90 text-gray-600 hover:text-amber-500 opacity-0 group-hover:opacity-100 focus:opacity-100'
                }`}
              >
                <Pin className={`w-3.5 h-3.5 ${doc.isPinned ? 'fill-current' : ''}`} />
                {doc.isPinned && 'Pinned'}
              </button>
            </div>
            <div className="p-4 text-center">
              <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{doc.title}</h3>
              {/* Order Dropdown in edit mode */}
              {editMode && (
                <div className="mt-2">
                  <label className={`block text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'} mb-1`}>Order</label>
                  <select
                    className={`w-full border ${darkMode ? 'border-gray-600 bg-gray-700 text-white' : 'border-gray-300 bg-white text-gray-900'} rounded-md px-2 py-1 text-sm`}
                    value={doc.order || ''}
                    onChange={e => handleOrderChange(doc.id, e.target.value)}
                  >
                    <option value="">Select order</option>
                    {[...Array(10)].map((_, i) => (
                      <option key={i+1} value={i+1}>{i+1}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
      
      {/* Empty State */}
      {filteredDocuments.length === 0 && (
        <div className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-8 text-center mt-6`}>
          <div className={`mb-4 mx-auto w-12 h-12 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center`}>
            <FileText className="h-6 w-6 text-gray-400" />
          </div>
          <h3 className={`text-lg font-medium ${darkMode ? 'text-white' : 'text-gray-900'} mb-1`}>No course templates found</h3>
          <p className={`${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Try adjusting your search or filter to find what you're looking for.</p>
        </div>
      )}

      {/* Add Course Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className={`bg-white dark:bg-gray-900 rounded-2xl shadow-xl w-full max-w-md`}>
            <div className="bg-red-600 rounded-t-lg p-4 flex flex-col items-center justify-center relative">
              <h2 className="text-xl font-bold text-white text-center">Add New Course</h2>
              <button 
                onClick={() => setShowAddModal(false)}
                className="absolute top-3 right-3 text-white hover:text-gray-200"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleAddCourse} className="p-6">
              <div className="space-y-4">
                <div>
                  <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-1`}>Title</label>
                  <input
                    type="text"
                    className={`w-full px-3 py-2 border ${darkMode ? 'border-gray-600 bg-gray-700 text-white' : 'border-gray-300 bg-white text-gray-900'} rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500`}
                    value={newCourse.title}
                    onChange={e => setNewCourse({ ...newCourse, title: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-1`}>Category</label>
                  <select
                    className={`w-full px-3 py-2 border ${darkMode ? 'border-gray-600 bg-gray-700 text-white' : 'border-gray-300 bg-white text-gray-900'} rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500`}
                    value={newCourse.category}
                    onChange={e => setNewCourse({ ...newCourse, category: e.target.value })}
                    required
                  >
                    {categories.map(category => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-1`}>Description</label>
                  <textarea
                    className={`w-full px-3 py-2 border ${darkMode ? 'border-gray-600 bg-gray-700 text-white' : 'border-gray-300 bg-white text-gray-900'} rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500`}
                    rows="3"
                    value={newCourse.description}
                    onChange={e => setNewCourse({ ...newCourse, description: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-1`}>Thumbnail Image</label>
                  <div className="relative">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleFileChange}
                      className={`w-full px-3 py-2 border ${darkMode ? 'border-gray-600 bg-gray-700 text-white' : 'border-gray-300 bg-white text-gray-900'} rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium ${darkMode ? 'file:bg-gray-600 file:text-white' : 'file:bg-gray-100 file:text-gray-700'} hover:file:bg-gray-200`}
                    />
                  </div>
                  <p className={`mt-1 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Recommended size: 800x600 pixels (4:3 ratio). Max file size: 5MB. Supported formats: JPEG, PNG, GIF, WebP
                  </p>
                  {thumbnailPreview && (
                    <div className="mt-2">
                      <img src={thumbnailPreview} alt="Thumbnail Preview" className="w-full h-32 object-cover rounded border" />
                      <p className={`mt-1 text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        Preview: {thumbnailFile?.name}
                      </p>
                    </div>
                  )}
                </div>
              </div>
              <div className="mt-6 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className={`px-4 py-2 border ${darkMode ? 'border-gray-600 text-gray-300 bg-gray-700 hover:bg-gray-600' : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'} rounded-md shadow-sm text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500`}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={uploading}
                  className={`px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 ${
                    uploading 
                      ? 'bg-gray-400 cursor-not-allowed' 
                      : 'bg-red-600 hover:bg-red-700'
                  }`}
                >
                  {uploading ? 'Uploading...' : 'Add Course'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* New Courses & Lessons Modal */}
      <Modal isOpen={showNewModal} onClose={() => setShowNewModal(false)} maxWidth="xl">
        <div className="flex items-center gap-2 mb-4">
          <Sparkles className="text-red-600 w-5 h-5" />
          <h2 className="text-lg font-semibold text-gray-800 dark:text-white">New Courses & Lessons</h2>
        </div>
        <div className="grid grid-cols-2 gap-3 justify-items-center">
          {recentFeed.map((item) => (
            <CompactFeedCard
              key={item.kind === 'course' ? `course-${item.courseId}` : `lesson-${item.courseId}-${item.lessonId}`}
              item={item}
              darkMode={darkMode}
            />
          ))}
        </div>
      </Modal>
      </div>
    </div>
  );
};

export default CourseTemplates;